import { clientLogger } from '#app/utils/pz/logger/clientLogger'

const log = clientLogger.scoped('TagManagerLoader')

declare global {
	interface Window {
		dataLayer: any[]
	}
}

export type LoadTagManagerArgs = {
	id: string
	dataLayer?: Record<string, unknown>
	enableConsentMode?: boolean
}

type LoaderState = 'idle' | 'loading' | 'loaded' | 'failed'

const SCRIPT_STATE_KEY = Symbol.for('puzzle.tagManager.loaderState')
const READY_FLAG_KEY = Symbol.for('puzzle.tagManager.consentReady')

type GlobalSlot = Record<PropertyKey, unknown> & {
	[SCRIPT_STATE_KEY]?: LoaderState
	[READY_FLAG_KEY]?: boolean
}

function globalSlot(): GlobalSlot {
	return globalThis as GlobalSlot
}

function getState(): LoaderState {
	return globalSlot()[SCRIPT_STATE_KEY] ?? 'idle'
}

function setState(state: LoaderState) {
	globalSlot()[SCRIPT_STATE_KEY] = state
}

function pushReadyPayloadOnce(
	id: string,
	dataLayer: Record<string, unknown> | undefined,
	enableConsentMode: boolean,
) {
	const slot = globalSlot()
	if (slot[READY_FLAG_KEY]) return

	if (typeof window === 'undefined') return

	window.dataLayer = window.dataLayer || []

	if (enableConsentMode) {
		window.dataLayer.push([
			'consent',
			'default',
			{
				analytics_storage: 'denied',
				ad_storage: 'denied',
				wait_for_update: 500,
			},
		])
	}

	window.dataLayer.push({
		tag_manager_id: id,
		environment:
			typeof process !== 'undefined' ? process.env?.NODE_ENV : undefined,
		...(dataLayer ?? {}),
		event: 'consent_initialized',
		consentGiven: true,
	})

	slot[READY_FLAG_KEY] = true
}

/**
 * Load the Google Tag Manager runtime exactly once.
 *
 * Behavior:
 * - Idempotent: repeated calls after the first successful load are no-ops.
 * - SSR-safe: returns early when `window` is undefined.
 * - Pure of decision: this loader does NOT consult consent state. The hook
 *   that invokes it is responsible for the gate (see {@link
 *   shouldLoadTagManager}).
 *
 * Resolves to `true` if the script was loaded (or had been loaded previously)
 * and `false` if it could not be loaded.
 */
export async function loadTagManager(
	args: LoadTagManagerArgs,
): Promise<boolean> {
	if (typeof window === 'undefined') return false
	if (!args.id) return false

	const state = getState()
	if (state === 'loaded') return true
	if (state === 'loading') return true

	setState('loading')

	const enableConsentMode = args.enableConsentMode ?? false

	pushReadyPayloadOnce(args.id, args.dataLayer, enableConsentMode)

	try {
		const TagManagerModule = await import('react-gtm-module')
		const TagManager = TagManagerModule.default
		TagManager.initialize({ gtmId: args.id })
		setState('loaded')
		return true
	} catch (error) {
		setState('failed')
		log.error(
			'Error al cargar TagManager',
			error instanceof Error ? error : { error: String(error) },
		)
		return false
	}
}

export function pushConsentUpdate(
	consentGiven: boolean,
	enableConsentMode: boolean,
) {
	if (typeof window === 'undefined') return
	if (!window.dataLayer) return

	window.dataLayer.push({
		event: 'consent_update',
		consentGiven,
	})

	if (enableConsentMode) {
		window.dataLayer.push([
			'consent',
			'update',
			{
				analytics_storage: consentGiven ? 'granted' : 'denied',
				ad_storage: 'denied',
			},
		])
	}
}

/**
 * Test-only helper. Resets the module-scoped loader state so tests can assert
 * the idempotency contract without leaking across cases.
 */
export function __resetTagManagerLoaderForTests() {
	const slot = globalSlot()
	delete slot[SCRIPT_STATE_KEY]
	delete slot[READY_FLAG_KEY]
}
